<template>
    <div class="flex flex-wrap">
        <div class="w-full flex">
            <div class="w-1/2">
                <div class="column-title">Data</div>
                <div>
                    <label class="inline font-normal" for="priceBox">
                        <input class="form-control mr-1" id="priceBox" v-model="form.tooltip" type="radio" value="standard">
                        Price Box
                    </label>
                </div>
                <div>
                    <label class="inline font-normal" for="top">
                        <input class="form-control mr-1" id="top" v-model="form.tooltip" type="radio" value="cards">
                        Top of Pane
                    </label>
                </div>
                <div>
                    <label class="inline font-normal" for="hide-tooltip">
                        <input class="form-control mr-1" id="hide-tooltip" v-model="form.tooltip" type="radio" value="external">
                        Hide
                    </label>
                </div>

                <div class="column-title">Visual Controls</div>
                <div>
                    <label for="crosshair" class="font-normal">
                        <input id="crosshair" value="both" type="checkbox" class="form-control mr-1" name="crosshair" v-model="form.crosshair">
                        Cross Hair
                    </label>
                </div>
                <div>
                    <label for="vertical" class="font-normal">
                        <input id="vertical" value="vertical" type="checkbox" class="form-control mr-1" name="vertical" v-model="form.vertical">
                        Vertical Line
                    </label>
                </div>
                <div>
                    <label for="trackball" class="font-normal">
                        <input id="trackball" type="checkbox" class="form-control mr-1" name="trackball" v-model="form.trackball">
                        Track Ball
                    </label>
                </div>
            </div>

            <div class="w-1/2">
                <div class="column-title">Price Labels</div>
                <div>
                    <label class="inline font-normal" for="overlap">
                        <input class="form-control mr-1" id="overlap" v-model="form.labels" @change="updateLabel" type="radio" value="overlap">
                        Overlap
                    </label>
                </div>
                <div>
                    <label class="inline font-normal" for="separate">
                        <input class="form-control mr-1" id="separate" v-model="form.labels" @change="updateLabel" type="radio" value="separate">
                        Separate
                    </label>
                </div>

                <div>
                    <label class="inline font-normal" for="hide-label">
                        <input class="form-control mr-1" id="hide-label" v-model="form.labels" @change="updateLabel" type="radio" value="main">
                        Hide
                    </label>
                </div>
                <div class="column-title">Grid Style</div>
                <div>
                    <label class="font-normal" for="gridLines">
                        <input id="gridLines" :disabled="form.minorGridLines" type="checkbox" class="form-control mr-1" name="gridLines" v-model="form.gridLines">
                        Basic Grid
                    </label>
                </div>
                <div>
                    <label class="font-normal" for="minorGridLines">
                        <input id="minorGridLines" @change="form.minorGridLines ? form.gridLines = true : ''" type="checkbox" class="form-control mr-1" name="minorGridLines" v-model="form.minorGridLines">
                        Minor Grid
                    </label>
                </div>
            </div>
        </div>

        <div class="w-full p-0">
            <div class="column-title">Bar Type</div>
            <select class="form-control form-control-sm" @change="setLineColors" v-model="form.line">
                <option value="OHLC" :disabled="form.scaleValue === 'percentChange'">OHLC</option>
                <option value="ColoredOHLC" :disabled="form.scaleValue === 'percentChange'">Colored OHLC</option>
                <option value="HLC" :disabled="form.scaleValue === 'percentChange'">HLC</option>

                <option value="HollowCandles" :disabled="form.scaleValue === 'percentChange'">Hollow Candles</option>
                <option value="CandlestickOpenToClose" :disabled="form.scaleValue === 'percentChange'">Candlestick Open-to-Close</option>
                <option value="CandlestickCloseToClose" :disabled="form.scaleValue === 'percentChange'">Candlestick Close-to-Close</option>
                <option value="HeikinAshi" :disabled="form.scaleValue === 'percentChange'">Heikin-Ashi</option>
                <option value="Line">Line</option>
                <option value="Area" :disabled="form.scaleValue === 'percentChange'">Area</option>
            </select>
            <div class="flex flex-wrap mt-2">
                <div class="inline-flex mr-1" v-for="(color, index) in form.lineColors" :class="{'sm:text-right': index==1}">
                    <!-- This is a quick hack. Candlestick charts have 2 additional colors of black for the border, we don't want them to appear here -->
                    <div v-if="index < 2" class="inline-flex">
                        <color-picker :initial-color="color" v-model="form.lineColors[index]"></color-picker>
                    </div>
                </div>
                <bar-thickness v-model="form.lineThickness" :initial-value="form.lineThickness"></bar-thickness>
            </div>
        </div>
        <div class="w-full p-0 mt-2">
            <div class="flex">
                <div class="w-full">
                    <label for="barDensity">Number of Bars</label>
                    <select id="barDensity" class="form-control form-control-sm" v-model.number="form.density">
                        <option :value="dense.value" v-for="dense in density">{{ dense.label }}</option>
                        <option value="custom">Custom</option>
                    </select>

                    <div class="mt-2" v-if="form.density === 'custom'">
                        <label for="customDensity">Custom Number of Bars</label>
                        <input id="customDensity" class="form-control form-control-sm" v-model.number="form.customDensity" placeholder="bars" type="number" name="" min="5" max="500">
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import ColorPicker from './ColorPicker';
import BarThickness from './BarThickness';

const props = defineProps({
    form: Object,
    modelValue: [String, Number, Object],
});

const emit = defineEmits(['update:modelValue']);

const lineColors = {
    OHLC: ['#000000'],
    ColoredOHLC: ["#EF4226", "#00B04B"],
    HLC: ["#EF4226", "#00B04B"],
    CandlestickOpenToClose: ["#EF4226", "#00B04B", "#00B04B", "#000000"],
    CandlestickCloseToClose: ["#EF4226", "#00B04B", "#00B04B", "#000000"],
    HollowCandles: ['#EF4226', '#000000'],
    HeikinAshi: ['#EF4226', '#00B04B'],
    Line: ['#000000'],
    Area: ['#4f97c1'],
};

const density = ref([
    { label: 'Minimum', value: 25 },
    { label: 'Medium', value: 75 },
    { label: 'High', value: 125 },
    { label: 'Maximum', value: 200 },
]);

const displayCustom = ref(false);

watch(() => props.form, (newValue) => {
    emit('update:modelValue', newValue);
}, { deep: true });

const setLineColors = () => {
    const style = props.form.line;

    // Vue 3 way of setting reactivity on an object property
    props.form.lineColors = lineColors[style];
};

const updateLabel = () => {
    if (props.form.labels !== 'main') {
        props.form.preventLabelOverlap = props.form.labels === 'separate' ? true : false;
    }
};

onMounted(() => {
    if (props.form.density) {
        const foundDensity = density.value.find(d => d.value === props.form.density);

        if (!foundDensity) {
            props.form.customDensity = props.form.density;
            props.form.density = 'custom';
        }
    }
});

onUnmounted(() => {
    if (props.form.density === 'custom') {
        props.form.density = props.form.customDensity;
    }
});
</script>
